import { ComponentVersions_3_0 } from 'constants/uxVersions'
import { orgSelectors } from 'ducks/orgs'
import { permissionsSelectors } from 'ducks/permissions'
import SupplierLogoWithFallback from 'elements/SupplierLogoWithFallback'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { BaseTheme, Button, ComponentVersionsInherit, styled, Typography } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { ActionType } from 'types/tracking'

const FlexWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: 24,
  flexWrap: 'wrap',
  padding: 20,
  backgroundColor: theme.palette.common.white,
}))

const SectionLeft = styled('section')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  maxWidth: 682,
  marginTop: 10,
})

const SectionRight = styled('section')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 8,
})

const LogoContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: `1px solid ${BaseTheme.palette.grey[400]}`,
  borderRadius: 4,
  width: 48,
  height: 30,
  padding: 8,
})

const ConnectButton = styled(Button)<{ disabled: boolean }>(({ theme, disabled }) => ({
  marginTop: 15,
  background: disabled ? `${theme.palette.grey[400]} !important` : `${theme.palette.blue[800]} !important`,
  color: `${theme.palette.common.white} !important`,
}))
const LogoGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(66px, 1fr))',
  height: 'fit-content',
  width: '100%',
  maxWidth: 'calc(66px * 6 + 8px * 5)',
  gap: 8,
})

const ConnectionBanner = () => {
  const history = useHistory()
  const translate = useTranslate()
  const enabledSellerIntegrationsConfig = useSelector(orgSelectors.getEnabledSellerIntegrationsConfig)
  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'connection_banner',
    eventName: 'Hardware Notification',
  })
  const { allowEdit: allowEditShop } = useSelector((state) =>
    permissionsSelectors.getPermissionByKey(state, 'shop_hardware_access')
  )

  const allSuppliersConnected = enabledSellerIntegrationsConfig.every((config) => config.isConnected)
  if (allSuppliersConnected) return null

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <FlexWrapper>
        <SectionLeft>
          <Typography variant="h6">{translate('Welcome to the OpenSolar Shop')}</Typography>
          <Typography variant="subtitle1" gutterBottom color="textSecondary">
            {translate('Log in now to access exclusive wholesale pricing available only in OpenSolar')}
          </Typography>
          <ConnectButton
            variant="contained"
            onClick={() => {
              trackUserInteraction({ type: ActionType.CLICK }, { source: 'marketplace_connect_button' })
              history.push('/shop_suppliers')
            }}
            disabled={!allowEditShop}
          >
            {translate('Connect your account')}
          </ConnectButton>
        </SectionLeft>
        <SectionRight>
          <LogoGrid>
            {enabledSellerIntegrationsConfig?.map((config) => (
              <LogoContainer key={config.filterKey}>
                <SupplierLogoWithFallback logoUrl={config.logoUrl} name={config.name} />
              </LogoContainer>
            ))}
          </LogoGrid>
        </SectionRight>
      </FlexWrapper>
    </ComponentVersionsInherit>
  )
}

export default ConnectionBanner
